import { Navigate } from "react-router";
import { useAuthContext } from "app/contexts/auth/context";
import { DynamicLayout } from "app/layouts/DynamicLayout";
import ReportsLayout from "app/layouts/ReportsLayout";
import AuthGuard from "middleware/AuthGuard";
import { hasPermission } from "utils/permissionsUtils";

// ----------------------------------------------------------------------

const RootRedirect = () => {
  if (hasPermission("DASDASView")) {
    return <Navigate to="/dashboards" replace />;
  }
  return <Navigate to="/jobs/schedules" replace />;
};

const protectedRoutes = {
  id: "protected",
  Component: AuthGuard,
  children: [
    // The dynamic layout supports both the main layout and the sideblock.
    {
      Component: DynamicLayout,
      children: [
        {
          index: true,
          element: <RootRedirect />,
        },
        {
          path: "notifications",
          lazy: async () => ({
            Component: (await import("components/leadUser/stages/NotificationsPage.jsx")).default,
          }),
        },
        {
          path: "no-access",
          lazy: async () => ({
            Component: (await import("app/pages/NoAccess")).default,
          }),
        },
        // ── Static Dashboard ────────────────────────────────────────────────────
        {
          path: "dashboards",
          lazy: async () => ({
            Component: (await import("app/pages/Dashboard/StaticDashboard")).default,
          }),
        },
        {
          path: "admin-dashboard",
          lazy: async () => ({
            Component: (await import("app/pages/Dashboard/AdminDashboard")).default,
          }),
        },
        // Leads
        {
          path: "leads",
          children: [
            {
              index: true,
              element: <Navigate to="/leads/opportunities" />,
            },
            {
              path: "opportunities",
              lazy: async () => ({
                Component: (await import("app/pages/leads/opportunities/OpportunitiesList")).default,
              }),
            },
            {
              path: "opportunities/:leadId",
              lazy: async () => ({
                Component: (await import("app/pages/leads/opportunities/OpportunitiesList")).default,
              }),
            },
            {
              path: "opportunities/:leadId/:tabName",
              lazy: async () => ({
                Component: (await import("app/pages/leads/opportunities/OpportunitiesList")).default,
              }),
            },
            {
              path: "activities",
              lazy: async () => ({
                Component: (await import("app/pages/leads/activities/ActivitiesList")).default,
              }),
            },
            {
              path: "activity-calendar",
              lazy: async () => ({
                Component: (await import("app/pages/myActivities")).default,
              }),
            },
            {
              path: "proposals",
              lazy: async () => ({
                Component: (await import("app/pages/leads/proposals/ProposalsList")).default,
              }),
            },
            {
              path: "details/:id",
              lazy: async () => ({
                Component: (await import("app/pages/leads/details")).default,
              }),
            },
          ],
        },
        // Jobs
        {
          path: "jobs",
          children: [
            {
              index: true,
              lazy: async () => ({
                Component: (await import("app/pages/jobs/list/JobsList")).default,
              }),
            },
            {
              path: ":jobId",
              lazy: async () => ({
                Component: (await import("app/pages/jobs/list/JobsList")).default,
              }),
            },
            {
              path: "schedules",
              lazy: async () => ({
                Component: (await import("app/pages/jobs/schedules/SchedulesLayout")).default,
              }),
              children: [
                {
                  index: true,
                  element: <Navigate to="/jobs/schedules/job-schedule" replace />,
                },
                {
                  path: "list",
                  lazy: async () => ({
                    Component: (await import("app/pages/jobs/schedules/SchedulesListTab")).default,
                  }),
                },
                {
                  path: "job-schedule",
                  lazy: async () => ({
                    Component: (await import("app/pages/jobs/schedules/JobScheduleTab")).default,
                  }),
                },
                {
                  path: "traders-suppliers",
                  lazy: async () => ({
                    Component: (await import("app/pages/jobs/schedules/TradersScheduleTab")).default,
                  }),
                },
                {
                  path: "workers-boys",
                  lazy: async () => ({
                    Component: (await import("app/pages/jobs/schedules/WorkersScheduleTab")).default,
                  }),
                },
              ]
            },
            {
              path: "jobs-calendar",
              element: <Navigate to="/jobs/my-calendar" replace />,
            },
            {
              path: "my-calendar",
              lazy: async () => ({
                Component: (await import("app/pages/myActivities")).default,
              }),
            },
            {
              path: "team-calendar",
              lazy: async () => ({
                Component: (await import("app/pages/myActivities")).default,
              }),
            },

            {
              path: "new/scratch",
              lazy: async () => ({
                Component: (await import("app/pages/jobs/new/scratch/CreateJobFromScratch")).default,
              }),
            },
            {
              path: "new/template",
              lazy: async () => ({
                Component: (await import("app/pages/jobs/new/template/CreateJobFromTemplate")).default,
              }),
            },
          ],
        },
        // Project Management
        {
          path: "project-management",
          children: [
            {
              path: "selections",
              lazy: async () => ({
                Component: (await import("app/pages/project-management/selections-v2/SelectionsPage")).default,
              }),
            },
            {
              path: "selections-v2",
              lazy: async () => ({
                Component: (await import("app/pages/project-management/selections-v2/SelectionsPage")).default,
              }),
            },
          ],
        },
        {
          path: "forms",
          children: [
            {
              index: true,
              element: <Navigate to="/forms/form-builder" />,
            },
            {
              path: "form-builder",
              lazy: async () => ({
                Component: (await import("app/pages/Builder")).default,
              }),
            },
            {
              path: "master-fields",
              lazy: async () => ({
                Component: (await import("app/pages/MasterFieldsManagement"))
                  .default,
              }),
            },
          ],
        },
        {
          path: "followups",
          children: [
            {
              index: true,
              element: <Navigate to="/followups/calendar" />,
            },
            {
              path: "calendar",
              lazy: async () => ({
                Component: (await import("app/pages/followups")).default,
              }),
            },
          ],
        },
        {
          path: "reports",
          Component: ReportsLayout,
          children: [
            {
              index: true,
              element: <Navigate to="/reports/lead-source" />,
            },
            {
              path: "lead-source",
              lazy: async () => ({
                Component: (
                  await import("app/pages/reports/LeadSourceReport")
                ).default,
              }),
            },
            {
              path: "lead-allocation",
              lazy: async () => ({
                Component: (
                  await import("app/pages/reports/LeadAllocationReport")
                ).default,
              }),
            },
            {
              path: "lead-status",
              lazy: async () => ({
                Component: (
                  await import("app/pages/reports/LeadStatusReport")
                ).default,
              }),
            },
            {
              path: "salesperson-performance",
              lazy: async () => ({
                Component: (
                  await import("app/pages/reports/SalespersonPerformance")
                ).default,
              }),
            },
            {
              path: "custom-comparison",
              lazy: async () => ({
                Component: (
                  await import("app/pages/reports/CustomComparisonReport")
                ).default,
              }),
            },
          ],
        },
        // WHS (Workplace Health & Safety)
        {
          path: "whs",
          lazy: async () => ({
            Component: (await import("app/layouts/WHSLayout")).default,
          }),
          children: [
            {
              index: true,
              element: <Navigate to="/whs/sign-in-out" />,
            },
            {
              path: "sign-in-out/:tab?/:jobId?",
              lazy: async () => ({
                Component: (await import("app/pages/whs/SignInOut")).default,
              }),
            },
            {
              path: "incident-report",
              lazy: async () => ({
                Component: (await import("app/pages/whs/IncidentReport")).default,
              }),
            },
            {
              path: "incident-report/:incidentId",
              lazy: async () => ({
                Component: (await import("app/pages/whs/IncidentReport")).default,
              }),
            },
            {
              path: "toolbox-talks",
              lazy: async () => ({
                Component: (await import("app/pages/whs/ToolboxTalks")).default,
              }),
            },
            {
              path: "toolbox-talks/:talkId",
              lazy: async () => ({
                Component: (await import("app/pages/whs/ToolboxTalks")).default,
              }),
            },
            {
              path: "vehicle-start-checklist",
              lazy: async () => ({
                Component: (await import("app/pages/whs/VehicleStartChecklist")).default,
              }),
            },
            {
              path: "vehicle-start-checklist/:checklistId",
              lazy: async () => ({
                Component: (await import("app/pages/whs/VehicleStartChecklist")).default,
              }),
            },
            {
              path: "vehicle-register",
              lazy: async () => ({
                Component: (await import("app/pages/whs/VehicleRegister")).default,
              }),
            },
            {
              path: "vehicle-register/:vehicleId",
              lazy: async () => ({
                Component: (await import("app/pages/whs/VehicleRegister")).default,
              }),
            },
            {
              path: "contractors-list",
              lazy: async () => ({
                Component: (await import("app/pages/whs/ContractorsList")).default,
              }),
            },
            {
              path: "contractors-list/:companyId",
              lazy: async () => ({
                Component: (await import("app/pages/whs/CompanyDetailsPage")).default,
              }),
            },

          ],
        },
        {
          path: "settings",
          lazy: async () => ({
            Component: (await import("app/pages/settings/Layout")).default,
          }),
          children: [
            {
              index: true,
              element: <Navigate to="/settings/general" />,
            },
            {
              path: "general",
              lazy: async () => ({
                Component: (
                  await import("app/pages/settings/sections/general/General")
                ).default,
              }),
            },
            {
              path: "appearance",
              lazy: async () => ({
                Component: (
                  await import(
                    "app/pages/settings/sections/appearance/Appearance"
                  )
                ).default,
              }),
            },
            {
              path: "user-management",
              lazy: async () => ({
                Component: (
                  await import(
                    "app/pages/settings/sections/UserManagement/UserManagement"
                  )
                ).default,
              }),
            },
            {
              path: "team-management",
              lazy: async () => ({
                Component: (
                  await import(
                    "app/pages/settings/sections/TeamManagement/TeamManagement"
                  )
                ).default,
              }),
            },
            {
              path: "designation-management",
              lazy: async () => ({
                Component: (
                  await import(
                    "app/pages/settings/sections/DesignationManagement/DesignationManagement"
                  )
                ).default,
              }),
            },
            {
              path: "template-management",
              lazy: async () => ({
                Component: (
                  await import(
                    "app/pages/settings/sections/TemplateManagement/TemplateManagement"
                  )
                ).default,
              }),
            },
            {
              path: "selection-management",
              lazy: async () => ({
                Component: (
                  await import(
                    "app/pages/settings/sections/SelectionManagement/SelectionManagementPage"
                  )
                ).default,
              }),
            },
            {
              path: "document-management",
              lazy: async () => ({
                Component: (
                  await import(
                    "app/pages/settings/sections/DocumentManagement/DocumentManagement"
                  )
                ).default,
              }),
            },
            {
              path: "tags-sources",
              lazy: async () => ({
                Component: (
                  await import(
                    "app/pages/settings/sections/TagsSources/TagsSourcesManagement"
                  )
                ).default,
              }),
            },
            {
              path: "contacts-management",
              lazy: async () => ({
                Component: (
                  await import(
                    "app/pages/settings/sections/ContactsManagement/ContactsManagement"
                  )
                ).default,
              }),
            },
            {
              path: "activity-types",
              lazy: async () => ({
                Component: (
                  await import(
                    "app/pages/settings/sections/ActivityTypesManagement/ActivityTypesManagement"
                  )
                ).default,
              }),
            },
            {
              path: "role-permissions",
              children: [
                {
                  index: true,
                  lazy: async () => ({
                    Component: (
                      await import(
                        "app/pages/settings/sections/RolePermissions/RolePermissions"
                      )
                    ).default,
                  }),
                },
                {
                  path: ":roleId",
                  lazy: async () => ({
                    Component: (
                      await import(
                        "app/pages/settings/sections/RolePermissions/RolePermissions"
                      )
                    ).default,
                  }),
                },
              ],
            },
            {
              path: "notification-management",
              lazy: async () => ({
                Component: (
                  await import(
                    "app/pages/settings/sections/NotificationManagement/NotificationManagement"
                  )
                ).default,
              }),
            },
            {
              path: "rule-management",
              lazy: async () => ({
                Component: (await import("app/pages/rules/RuleManagement"))
                  .default,
              }),
            },
            {
              path: "integrations",
              lazy: async () => ({
                Component: (
                  await import(
                    "app/pages/settings/sections/integrations/Integrations"
                  )
                ).default,
              }),
            },
            {
              path: "lead-stages",
              lazy: async () => ({
                Component: (
                  await import(
                    "app/pages/settings/sections/LeadStageManagement/LeadStageManagement"
                  )
                ).default,
              }),
            },
            {
              path: "api-keys",
              lazy: async () => ({
                Component: (
                  await import("app/pages/settings/sections/apiKeys/ApiKeys")
                ).default,
              }),
            },
            {
              path: "branding",
              lazy: async () => ({
                Component: (
                  await import("app/pages/settings/sections/builder/Builder")
                ).default,
              }),
            },

          ],
        },

      ],
    },
  ],
};

export { protectedRoutes };
